import type { QueryClient } from '@tanstack/react-query';
import { ensureAsciiDateString } from '../../shared/utils/apiParams';
import {
  isMenuInvalidationAllowedForDate,
  menuQueryPredicateSkipPastDates,
} from '../domains/menu/cache';
import { keys, monthFromDateYmd } from './keyRegistry';
import {
  resolveInvalidationPlan,
  type InvalidationParams,
  type InvalidationPlan,
  type InvalidationRuleId,
} from './invalidateRegistry';

async function executePlan(
  query_client: QueryClient,
  customer_id: number,
  plan: InvalidationPlan,
): Promise<void> {
  const tasks: Promise<void>[] = [];
  const customer_keys = keys.customer(customer_id);

  if (plan.subscription) {
    tasks.push(query_client.invalidateQueries({ queryKey: customer_keys.subscription }));
  }

  if (plan.subscription_pending) {
    tasks.push(query_client.invalidateQueries({ queryKey: customer_keys.subscriptionPending }));
  }

  const calendar_opt = plan.calendar;
  if (calendar_opt === true || calendar_opt === 'all') {
    tasks.push(query_client.invalidateQueries({ queryKey: customer_keys.calendarAll }));
  } else if (Array.isArray(calendar_opt)) {
    for (const month of calendar_opt) {
      tasks.push(
        query_client.invalidateQueries({
          queryKey: customer_keys.calendar(month),
        }),
      );
    }
  }

  if (plan.menu_all) {
    tasks.push(
      query_client.invalidateQueries({
        predicate: menuQueryPredicateSkipPastDates(customer_id),
      }),
    );
  } else if (plan.menu_dates?.length) {
    for (const date_raw of plan.menu_dates) {
      const date_ascii = ensureAsciiDateString(date_raw) || date_raw;
      if (!isMenuInvalidationAllowedForDate(date_ascii)) {
        continue;
      }
      tasks.push(
        query_client.invalidateQueries({
          queryKey: customer_keys.menu(date_ascii, false),
        }),
      );
      tasks.push(
        query_client.invalidateQueries({
          queryKey: customer_keys.menu(date_ascii, true),
        }),
      );
    }
  }

  await Promise.all(tasks);
}

/**
 * Apply a registered invalidation rule (replacement path only).
 */
export async function applyInvalidation(
  query_client: QueryClient,
  rule_id: InvalidationRuleId,
  params: InvalidationParams,
): Promise<void> {
  const plan = resolveInvalidationPlan(rule_id, params);
  await executePlan(query_client, params.customer_id, plan);
}

export { executePlan, resolveInvalidationPlan };
